// frontend/src/components/admin/UserRoleManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Security as SecurityIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import userService from '../../services/userServices';
import roleService from '../../services/roleService';

/**
 * Composant pour la gestion des utilisateurs et leurs rôles
 * @returns {React.ReactNode} - Composant de gestion des utilisateurs et leurs rôles
 */
const UserRoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Charger les utilisateurs et les rôles au montage du composant
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  /**
   * Récupérer tous les utilisateurs
   */
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer tous les rôles
   */
  const fetchRoles = async () => {
    try {
      const data = await roleService.getAllRoles();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  /**
   * Ouvrir le dialogue de gestion des rôles
   * @param {Object} user - Utilisateur dont on veut gérer les rôles
   */
  const handleOpenRolesDialog = (user) => {
    setSelectedUser(user);
    
    // Préparer la liste des rôles sélectionnés
    const userRoleIds = user.roles.map(r => r.id);
    setSelectedRoles(userRoleIds);
    
    setOpenDialog(true);
  };

  /**
   * Fermer le dialogue
   */
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setSelectedRoles([]);
  };

  /**
   * Gérer la sélection/désélection d'un rôle
   * @param {string} roleId - ID du rôle
   */
  const handleToggleRole = (roleId) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  /**
   * Soumettre les rôles de l'utilisateur
   */
  const handleSubmitRoles = async () => {
    try {
      // Récupérer les rôles actuels de l'utilisateur
      const currentRoleIds = selectedUser.roles.map(r => r.id);
      
      // Rôles à ajouter
      const rolesToAdd = selectedRoles.filter(
        id => !currentRoleIds.includes(id)
      );
      
      // Rôles à supprimer
      const rolesToRemove = currentRoleIds.filter(
        id => !selectedRoles.includes(id)
      );
      
      // Ajouter les nouveaux rôles
      for (const roleId of rolesToAdd) {
        await roleService.assignRoleToUser(selectedUser.id, roleId);
      }
      
      // Supprimer les rôles révoqués
      for (const roleId of rolesToRemove) {
        await roleService.revokeRoleFromUser(selectedUser.id, roleId);
      }
      
      showNotification('Rôles de l\'utilisateur mis à jour avec succès', 'success');
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user roles:', error);
      showNotification('Erreur lors de la mise à jour des rôles de l\'utilisateur', 'error');
    }
  };

  /**
   * Filtrer les utilisateurs par terme de recherche
   */
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  /**
   * Afficher une notification
   * @param {string} message - Message à afficher
   * @param {string} severity - Type de notification ('success', 'error', 'warning', 'info')
   */
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  /**
   * Fermer la notification
   */
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Gestion des Utilisateurs et Rôles
        </Typography>
        <TextField
          placeholder="Rechercher un utilisateur..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôles</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.roles.map((role) => (
                        <Chip
                          key={role.id}
                          label={role.role}
                          size="small"
                          color={
                            role.role === 'super_admin' ? 'error' :
                            role.role === 'owner' ? 'primary' :
                            role.role === 'tenant' ? 'success' : 'default'
                          }
                        />
                      ))}
                      {user.isAdmin && !user.roles.some(r => r.role === 'super_admin') && (
                        <Chip label="admin" size="small" color="error" />
                      )}
                      {user.isOwner && !user.roles.some(r => r.role === 'owner') && (
                        <Chip label="owner" size="small" color="primary" />
                      )}
                      {user.isTenant && !user.roles.some(r => r.role === 'tenant') && (
                        <Chip label="tenant" size="small" color="success" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.verificationStatus || 'verified'}
                      color={
                        user.verificationStatus === 'verified' ? 'success' :
                        user.verificationStatus === 'pending' ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenRolesDialog(user)}
                    >
                      <SecurityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucun utilisateur trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogue de gestion des rôles */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Rôles de l'utilisateur: {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          <List>
            {roles.map((role) => (
              <React.Fragment key={role.id}>
                <ListItem>
                  <ListItemText
                    primary={role.role}
                    secondary={role.description}
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge="end"
                      checked={selectedRoles.includes(role.id)}
                      onChange={() => handleToggleRole(role.id)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleSubmitRoles} color="primary" variant="contained">
            Enregistrer les rôles
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRoleManagement;